import { Component, OnDestroy, OnInit } from '@angular/core';

// Rxjs
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// Shared
import { MESSAGE_ALGO_SALIO_MAL, show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

// Services
import { AuthService } from '@auth/auth.service';
import { HistoricService } from './historic.service';
import { FileDownloaderService } from '@shared/utility/file-downloader.service';
import { DetailCreditService } from '@home/services/detail-credit-service/detail-credit.service';

// Models
import { ClearanceLetterResponseType } from '@model/historicResponse';
import { LoanHistoryElementType } from './models';

@Component({
  selector: 'app-historic',
  templateUrl: './historic.component.html',
  styleUrls: ['./historic.component.css'],
})
export class HistoricComponent implements OnInit, OnDestroy {
  public showLoader: boolean;
  public historicList: LoanHistoryElementType[];
  public hideButton: boolean;
  public noShowDownloadHistoryButton: boolean;
  public userName: string;

  private readonly unSubscribe$: Subject<void>;

  constructor(
    private readonly authService: AuthService,
    private readonly loanHistoricService: HistoricService,
    private readonly fileService: FileDownloaderService,
    private readonly detailCreditService: DetailCreditService
  ) {
    this.hideButton = false;
    this.unSubscribe$ = new Subject();
  }

  public ngOnInit(): void {
    this.showLoader = true;
    this.getHistoric();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.unsubscribe();
  }

  private getHistoric(): void {
    this.userName = this.authService.getTokenData(this.authService.token)?.user_name;
    this.loanHistoricService
      .getLoanHistory()
      .pipe(
        takeUntil(this.unSubscribe$),
        finalize(() => {
          this.showLoader = false;
        })
      )
      .subscribe({
        next: (response: LoanHistoryElementType[]) => {
          this.historicList = this.buildHistoricList(response);
        },
        error: () => {
          show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL);
        },
      });
  }

  private buildHistoricList(loanList: LoanHistoryElementType[]): LoanHistoryElementType[] {
    return loanList.map((loan) => ({
      ...loan,
      paymentDate: loan.label ? loan.label.toUpperCase() : this.getFullDate(loan.paymentDate),
    })) as unknown as LoanHistoryElementType[];
  }

  public downloadClearanceLetterCertificate(loanCode: number): void {
    this.showLoader = true;
    this.detailCreditService
      .buildClearanceLetterCertificate(loanCode, this.authService.token)
      .pipe(
        takeUntil(this.unSubscribe$),
        finalize(() => (this.showLoader = false))
      )
      .subscribe({
        next: ({ document, name, type }: ClearanceLetterResponseType) => {
          this.fileService.downloadPDF(document, name + '.' + type);
        },
        error: () => {
          show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL);
        },
      });
  }

  private getFullDate(paymentDate: Date | number): string {
    if (!paymentDate) {
      return '';
    }

    let date: Date | number = new Date(paymentDate);

    return `${date.getDate()} ${date.toLocaleString('default', { month: 'long' }).slice(0, 3)}. ${date.getFullYear()}`;
  }
}
